import React, { useState, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Close,
  Delete,
  Download,
  PictureAsPdf,
  Image as ImageIcon,
  CloudUpload,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const TIPOS_DOCUMENTOS = [
  { value: 'cpp', label: '1. Certificados CPP', requiresExpiry: false },
  { value: 'gmp', label: '2. GMP (ISO, OMS, FDA, EMA)', requiresExpiry: false },
  { value: 'autorizacion', label: '3. Carta de Autorización', requiresExpiry: false },
  { value: 'distribucion', label: '4. Carta de Distribución', requiresExpiry: false },
  { value: 'poder', label: '5. Carta Poder', requiresExpiry: false },
  { value: 'ficha_tecnica', label: '6. Fichas Técnicas', requiresExpiry: false },
  { value: 'hoja_seguridad', label: '7. Hojas de Seguridad', requiresExpiry: false },
  { value: 'monografia', label: '8. Monografías', requiresExpiry: false },
  { value: 'certificado_calidad', label: '9. Certificados de Calidad', requiresExpiry: false },
  { value: 'etiquetas', label: '10. Etiquetas (Primarias, Secundarias, Terciarias)', requiresExpiry: false },
  { value: 'estudio_clinico', label: '11. Estudios Clínicos', requiresExpiry: false },
  { value: 'bioequivalencia', label: '12. Estudios de Bioequivalencia', requiresExpiry: false },
];

const ProductoDetalle = ({ producto, onClose }) => {
  const [imagenes, setImagenes] = useState({
    imagen_1: null,
    imagen_2: null,
    imagen_3: null,
    imagen_4: null,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (producto) {
      // No usamos producto.documentos porque ahora los documentos están en campos document1-48
      setImagenes({
        imagen_1: producto.image1 || producto.imagen_1,
        imagen_2: producto.image2 || producto.imagen_2,
        imagen_3: producto.image3 || producto.imagen_3,
        imagen_4: producto.image4 || producto.imagen_4,
      });
    }
  }, [producto]);

  const handleUploadImage = async (numero, file) => {
    if (!file) return;

    try {
      const token = localStorage.getItem('token');

      const formData = new FormData();
      formData.append('imagen', file);

      const response = await fetch(`/api/productos/${producto.id}/imagen/${numero}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setImagenes({ ...imagenes, [`imagen_${numero}`]: data[`imagen_${numero}`] });
        setSuccess(`Imagen ${numero} subida correctamente`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Error al subir imagen');
      }
    } catch (error) {
      setError('Error al subir imagen');
    }
  };

  const handleDeleteImage = async (numero) => {
    if (!window.confirm('¿Estás seguro de eliminar esta imagen?')) return;

    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/productos/${producto.id}/imagen/${numero}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setImagenes({ ...imagenes, [`imagen_${numero}`]: null });
        setSuccess(`Imagen ${numero} eliminada correctamente`);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      setError('Error al eliminar imagen');
    }
  };

  // Ya no se usan estas funciones porque los documentos se manejan desde ProductoForm
  // const handleUploadDocumento = async (imagenNumero, tipoDocumento, file, fechaVencimiento = '') => { ... };
  // const handleDeleteDocumento = async (id) => { ... };

  const getDocumentoByImageAndType = (imageNumber, tipoDocIndex) => {
    // Calcular el número de documento: Imagen 1 = docs 1-12, Imagen 2 = docs 13-24, etc.
    const docNumber = ((imageNumber - 1) * 12) + (tipoDocIndex + 1);
    const documentPath = producto?.[`document${docNumber}`];
    const documentName = producto?.[`documentName${docNumber}`];

    if (documentPath) {
      return {
        ruta_archivo: documentPath,
        nombre_archivo: documentName || `Documento ${docNumber}`
      };
    }
    return null;
  };

  // Ya no se usa porque no se muestran fechas de vencimiento en la vista de detalle
  // const getDocumentoVencimientoChip = (fechaVencimiento) => { ... };

  const DocumentoField = ({ imagenNumero, tipoDocInfo, tipoDocIndex }) => {
    const documento = getDocumentoByImageAndType(imagenNumero, tipoDocIndex);

    return (
      <Box
        sx={{
          p: 2,
          mb: 1.5,
          borderRadius: '12px',
          border: '1px solid',
          borderColor: documento ? '#4caf50' : '#e0e0e0',
          bgcolor: documento ? 'rgba(76, 175, 80, 0.05)' : 'rgba(0,0,0,0.01)',
          transition: 'all 0.3s',
          '&:hover': {
            borderColor: '#3b82f6',
            bgcolor: 'rgba(59, 130, 246, 0.02)',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
            {tipoDocInfo.label}
            {tipoDocInfo.requiresExpiry && (
              <Chip
                label="Vencimiento requerido"
                size="small"
                color="warning"
                sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
              />
            )}
          </Typography>
        </Box>

        {documento ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PictureAsPdf color="error" sx={{ fontSize: 18 }} />
              <Typography variant="caption" sx={{ flex: 1, fontWeight: 500 }}>
                {documento.nombre_archivo}
              </Typography>
              <Chip
                label="Guardado"
                size="small"
                color="success"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
              <IconButton
                size="small"
                onClick={() => window.open(`${documento.ruta_archivo}`, '_blank')}
                sx={{ p: 0.5 }}
                title="Ver documento"
              >
                <Download sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <Box sx={{
            p: 1.5,
            borderRadius: '8px',
            bgcolor: 'rgba(0,0,0,0.03)',
            textAlign: 'center'
          }}>
            <Typography variant="caption" color="text.secondary">
              Sin documento
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  if (!producto) return null;

  return (
    <>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Detalles del Producto
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <AnimatePresence>
          {(error || success) && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Alert
                severity={error ? 'error' : 'success'}
                onClose={() => error ? setError('') : setSuccess('')}
                sx={{ mb: 3, borderRadius: '12px' }}
              >
                {error || success}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Información del Producto */}
        <Card sx={{ mb: 4, borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={3}>
              {/* Registro Sanitario - Solo mostrar si NO es descartable */}
              {!producto.es_descartable && producto.numero_registro_sanitario && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Registro Sanitario
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {producto.numero_registro_sanitario}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                      label={producto.estado_registro === 'vigente' ? 'Vigente' : producto.estado_registro === 'vencido' ? 'Vencido' : 'Vigente'}
                      size="small"
                      color={producto.estado_registro === 'vigente' ? 'success' : 'error'}
                    />
                    {producto.vencimiento_registro && (
                      <Typography variant="caption" color="text.secondary">
                        Vence: {new Date(producto.vencimiento_registro).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Nombre del Producto */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Nombre del Producto
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: '#1976d2' }}>
                  {producto.nombre}
                </Typography>
              </Grid>

              {/* Código SAP */}
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Código SAP
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {producto.sapCode || producto.codigo_sap || 'N/A'}
                </Typography>
              </Grid>

              {/* Descripción de la Presentación */}
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Descripción de la Presentación
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {producto.description || producto.descripcion_presentacion || producto.descripcion || 'N/A'}
                </Typography>
              </Grid>

              {/* Laboratorio Fabricante */}
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Laboratorio Fabricante
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.5 }}>
                  {producto.manufacturer || producto.laboratorio_fabricante || 'N/A'}
                </Typography>
              </Grid>

              {/* Categoría */}
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Categoría
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={producto.category || producto.categoria || 'No controlado'}
                    color={
                      (producto.category || producto.categoria) === 'Controlado' ? 'error' :
                      (producto.category || producto.categoria) === 'Descartable' ? 'warning' :
                      'default'
                    }
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Grid>

              {/* Vida Útil */}
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Vida Útil
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {producto.shelfLife || producto.vida_util || 'N/A'}
                </Typography>
              </Grid>

              {/* País de Origen */}
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  País de Origen
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {producto.originCountry || producto.pais_origen || 'N/A'}
                </Typography>
              </Grid>

              {/* Código del Proveedor */}
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Código del Proveedor
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.5 }}>
                  {producto.providerCode || producto.codigo_proveedor || 'N/A'}
                </Typography>
              </Grid>

              {/* Estado */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Estado
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={
                      (producto.status || producto.estado) === 'activo' ? 'Activo' :
                      (producto.status || producto.estado) === 'inactivo' ? 'Inactivo' :
                      producto.estado === 'descontinuado' ? 'Descontinuado' :
                      'Activo'
                    }
                    color={
                      producto.estado === 'activo' ? 'success' :
                      producto.estado === 'inactivo' ? 'default' :
                      'error'
                    }
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 4 Imágenes del Producto con Lotes */}
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
          Imágenes del Producto (4 lotes máximo)
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Cada imagen puede tener: número de lote, fecha de vencimiento y certificado de análisis
        </Typography>

        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((numero) => (
            <Grid item xs={12} md={6} key={numero}>
              <Card sx={{ borderRadius: '16px' }}>
                <CardContent>
                  {/* Imagen */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Imagen {numero}
                      </Typography>
                    </Box>

                    {imagenes[`imagen_${numero}`] ? (
                      <Box>
                        <Box
                          component="img"
                          src={`${imagenes[`imagen_${numero}`]}`}
                          alt={`Producto imagen ${numero}`}
                          sx={{
                            width: '100%',
                            height: 250,
                            objectFit: 'cover',
                            borderRadius: '12px',
                            border: '2px solid #e0e0e0',
                          }}
                        />
                        {/* Botón de descarga */}
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                          <Button
                            variant="contained"
                            startIcon={<Download />}
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = imagenes[`imagen_${numero}`];
                              link.download = `${producto.nombre || 'producto'}_imagen_${numero}.png`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            sx={{
                              background: 'linear-gradient(135deg, #1976d2 0%, #2196f3 100%)',
                              color: 'white',
                              fontWeight: 600,
                              padding: '8px 24px',
                              borderRadius: '8px',
                              textTransform: 'none',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                              },
                              transition: 'all 0.3s ease'
                            }}
                          >
                            Descargar Imagen {numero}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: 250,
                          borderRadius: '12px',
                          border: '2px dashed #cbd5e1',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(0,0,0,0.02)',
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="caption" color="text.secondary">
                          Sin imagen
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Información del Lote */}
                  {(producto[`lot${numero}Number`] || producto[`lot${numero}`]) && (
                    <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: 'rgba(33, 150, 243, 0.05)', borderRadius: '8px' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
                        Información del Lote {numero}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Número de Lote:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {producto[`lot${numero}Number`] || producto[`lot${numero}`]}
                          </Typography>
                        </Box>

                        {producto[`lot${numero}ExpiryDate`] && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Fecha de Vencimiento:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {new Date(producto[`lot${numero}ExpiryDate`]).toLocaleDateString('es-ES', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </Typography>
                          </Box>
                        )}

                        {producto[`lot${numero}AnalysisCert`] && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                              Certificado de Análisis:
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PictureAsPdf sx={{ fontSize: 20, color: '#1976d2' }} />
                              <Typography variant="caption" sx={{ flex: 1, fontSize: '0.75rem', color: '#1976d2' }}>
                                {producto[`lot${numero}AnalysisCertName`] || producto[`lot${numero}AnalysisCert`].split('/').pop() || 'Certificado de Análisis'}
                              </Typography>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Download />}
                                href={producto[`lot${numero}AnalysisCert`]}
                                target="_blank"
                                sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                              >
                                Ver
                              </Button>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* DOCUMENTOS POR LOTE OCULTADOS - Ahora son globales */}
                  <Box sx={{ display: 'none' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                      Documentos (12 campos)
                    </Typography>
                    <Box sx={{ maxHeight: 600, overflowY: 'auto', pr: 1 }}>
                      {TIPOS_DOCUMENTOS.map((tipoDoc, index) => (
                        <DocumentoField
                          key={tipoDoc.value}
                          imagenNumero={numero}
                          tipoDocInfo={tipoDoc}
                          tipoDocIndex={index}
                        />
                      ))}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Documentos Globales del Producto */}
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, mt: 4 }}>
          Documentos
        </Typography>

        <Grid container spacing={2}>
          {TIPOS_DOCUMENTOS.map((tipoDoc, index) => {
            const docNumber = index + 1; // 1-12
            const documentPath = producto[`document${docNumber}`];
            const documentName = producto[`documentName${docNumber}`];
            const documentExpiry = producto[`document${docNumber}ExpiryDate`];

            return (
              <Grid item xs={12} md={6} key={tipoDoc.value}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: documentPath ? '#4caf50' : '#e0e0e0',
                    bgcolor: documentPath ? 'rgba(76, 175, 80, 0.05)' : 'transparent',
                    minHeight: 80,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      Documento {docNumber}
                    </Typography>
                  </Box>

                  {documentPath ? (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <PictureAsPdf sx={{ fontSize: 20, color: '#1976d2' }} />
                        <Typography variant="caption" sx={{ flex: 1, fontSize: '0.75rem', color: '#1976d2' }}>
                          {documentName || 'Documento existente'}
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Download />}
                          href={documentPath}
                          target="_blank"
                          sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                        >
                          Ver
                        </Button>
                      </Box>

                      {documentExpiry && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                          Vencimiento: {new Date(documentExpiry).toLocaleDateString('es-ES', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      Sin documento
                    </Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>

      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: '12px', textTransform: 'none' }}>
          Cerrar
        </Button>
      </DialogActions>
    </>
  );
};

export default ProductoDetalle;
